import React, { useState, useEffect } from 'react';
import { Campaign, Site, Technicentre, Prestation } from '../../types';
import { campaignsApi, sitesApi, technicentresApi, prestationsApi } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useData } from '../../contexts/DataContext';

interface GOFPanelProps {
  show: boolean;
  onClose: () => void;
  isGOF?: boolean;
}

// ===== Composant GOFPanel =====
const GOFPanel: React.FC<GOFPanelProps> = ({ show, onClose, isGOF = true }) => {
  const {
    campaigns,
    sites,
    technicentres,
    prestations,
    refreshCampaigns,
    refreshSites,
    refreshTechnicentres,
    refreshPrestations
  } = useData();

  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showSiteModal, setShowSiteModal] = useState(false);
  const [showTechnicentreModal, setShowTechnicentreModal] = useState(false);
  const [showPrestationModal, setShowPrestationModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const [editingItem, setEditingItem] = useState<any>(null);

  const { showNotification } = useNotification();

  const handleCampaignSave = async (data: { nom: string }) => {
    try {
      if (editingItem) {
        await campaignsApi.update(editingItem.id, data);
        showNotification('Campagne modifiée', 'success');
      } else {
        await campaignsApi.create(data);
        showNotification('Campagne ajoutée', 'success');
      }
      setShowCampaignModal(false);
      setEditingItem(null);
      await refreshCampaigns();
    } catch (error) {
      showNotification('Erreur lors de l\'enregistrement', 'error');
    }
  };

  const handleCampaignDelete = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette campagne ?')) return;

    try {
      await campaignsApi.delete(id);
      showNotification('Campagne supprimée', 'success');
      await refreshCampaigns();
    } catch (error) {
      showNotification('Erreur lors de la suppression', 'error');
    }
  };

  const handleSiteSave = async (data: { nom: string }) => {
    try {
      if (editingItem) {
        await sitesApi.update(editingItem.id, data);
        showNotification('Lieu modifié', 'success');
      } else {
        await sitesApi.create(data);
        showNotification('Lieu ajouté', 'success');
      }
      setShowSiteModal(false);
      setEditingItem(null);
      await refreshSites();
    } catch (error) {
      showNotification('Erreur lors de l\'enregistrement', 'error');
    }
  };

  const handleSiteDelete = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce lieu ?')) return;

    try {
      await sitesApi.delete(id);
      showNotification('Lieu supprimé', 'success');
      await refreshSites();
    } catch (error) {
      showNotification('Erreur lors de la suppression', 'error');
    }
  };

  const handleTechnicentreSave = async (data: { nom: string; email?: string }) => {
    try {
      if (editingItem) {
        await technicentresApi.update(editingItem.id, data);
        showNotification('Technicentre modifié', 'success');
      } else {
        await technicentresApi.create(data);
        showNotification('Technicentre ajouté', 'success');
      }
      setShowTechnicentreModal(false);
      setEditingItem(null);
      await refreshTechnicentres();
    } catch (error) {
      showNotification('Erreur lors de l\'enregistrement', 'error');
    }
  };

  const handleTechnicentreDelete = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce technicentre ?')) return;

    try {
      await technicentresApi.delete(id);
      showNotification('Technicentre supprimé', 'success');
      await refreshTechnicentres();
    } catch (error) {
      showNotification('Erreur lors de la suppression', 'error');
    }
  };

  const handlePrestationSave = async (data: { nom: string }) => {
    try {
      if (editingItem) {
        await prestationsApi.update(editingItem.id, data);
        showNotification('Prestation modifiée', 'success');
      } else {
        await prestationsApi.create(data);
        showNotification('Prestation ajoutée', 'success');
      }
      setShowPrestationModal(false);
      setEditingItem(null);
      await refreshPrestations();
    } catch (error) {
      showNotification('Erreur lors de l\'enregistrement', 'error');
    }
  };

  const handlePrestationDelete = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette prestation ?')) return;

    try {
      await prestationsApi.delete(id);
      showNotification('Prestation supprimée', 'success');
      await refreshPrestations();
    } catch (error) {
      showNotification('Erreur lors de la suppression', 'error');
    }
  };

  const handlePasswordUpdate = async (technicentreId: number, newPassword: string) => {
    try {
      await technicentresApi.updatePassword(technicentreId, newPassword);
      showNotification('Mot de passe modifié avec succès', 'success');
      setShowPasswordModal(false);
      setEditingItem(null);
    } catch (error) {
      showNotification('Erreur lors de la modification du mot de passe', 'error');
    }
  };

  if (!show) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content modal-xl" onClick={e => e.stopPropagation()}>
        <div className="relative mb-6">
          <button onClick={onClose} className="modal-close">
            <i className="fas fa-times"></i>
          </button>
          <h3 className="text-2xl font-bold text-dark flex items-center">
            <i className="fas fa-cogs mr-3 text-purple"></i>
            Paramètres LGV
          </h3>
        </div>

        <div className="gof-container">
          <div className="gof-grid">
          <div className={`gof-card ${!isGOF ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="gof-card-header">
              <h3 className={`gof-card-title ${!isGOF ? 'text-gray-400' : ''}`}>
                <i className={`fas fa-flag ${!isGOF ? 'text-gray-400' : 'text-blue-600'}`}></i> Campagnes
                {!isGOF && <span className="text-xs text-gray-500 ml-2">(GOF uniquement)</span>}
              </h3>
              <button
                onClick={() => {
                  if (!isGOF) return;
                  setEditingItem(null);
                  setShowCampaignModal(true);
                }}
                className={`btn ${!isGOF ? 'btn-secondary cursor-not-allowed' : 'btn-primary'}`}
                style={{fontSize: '0.875rem', padding: '0.5rem 1rem'}}
                disabled={!isGOF}
              >
                <i className="fas fa-plus mr-1"></i> Ajouter
              </button>
            </div>
            <div className="gof-list">
              {campaigns.length === 0 ? (
                <p className={`empty-state ${!isGOF ? 'text-gray-400' : ''}`}>Aucune campagne</p>
              ) : (
                campaigns.map((campaign) => (
                  <div key={campaign.id} className={`gof-list-item ${!isGOF ? 'opacity-60' : ''}`}>
                    <div className="gof-list-item-content">
                      <p className={!isGOF ? 'text-gray-400' : ''}>{campaign.nom}</p>
                    </div>
                    {isGOF && (
                      <div className="gof-list-item-actions">
                        <button
                          onClick={() => {
                            setEditingItem(campaign);
                            setShowCampaignModal(true);
                          }}
                          className="edit-btn"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          onClick={() => handleCampaignDelete(campaign.id)}
                          className="delete-btn"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="gof-card">
            <div className="gof-card-header">
              <h3 className="gof-card-title">
                <i className="fas fa-map-marker-alt text-green-600"></i> Lieux d'intervention
              </h3>
              <button
                onClick={() => {
                  setEditingItem(null);
                  setShowSiteModal(true);
                }}
                className="btn btn-primary"
                style={{fontSize: '0.875rem', padding: '0.5rem 1rem'}}
              >
                <i className="fas fa-plus mr-1"></i> Ajouter
              </button>
            </div>
            <div className="gof-list">
              {sites.length === 0 ? (
                <p className="empty-state">Aucun lieu</p>
              ) : (
                sites.map((site) => (
                  <div key={site.id} className="gof-list-item">
                    <div className="gof-list-item-content">
                      <p>{site.nom}</p>
                    </div>
                    <div className="gof-list-item-actions">
                      <button
                        onClick={() => {
                          setEditingItem(site);
                          setShowSiteModal(true);
                        }}
                        className="edit-btn"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        onClick={() => handleSiteDelete(site.id)}
                        className="delete-btn"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className={`gof-card ${!isGOF ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="gof-card-header">
              <h3 className={`gof-card-title ${!isGOF ? 'text-gray-400' : ''}`}>
                <i className={`fas fa-building ${!isGOF ? 'text-gray-400' : 'text-orange-600'}`}></i> Technicentres
                {!isGOF && <span className="text-xs text-gray-500 ml-2">(GOF uniquement)</span>}
              </h3>
              <button
                onClick={() => {
                  if (!isGOF) return;
                  setEditingItem(null);
                  setShowTechnicentreModal(true);
                }}
                className={`btn ${!isGOF ? 'btn-secondary cursor-not-allowed' : 'btn-primary'}`}
                style={{fontSize: '0.875rem', padding: '0.5rem 1rem'}}
                disabled={!isGOF}
              >
                <i className="fas fa-plus mr-1"></i> Ajouter
              </button>
            </div>
            <div className="gof-list">
              {technicentres.length === 0 ? (
                <p className={`empty-state ${!isGOF ? 'text-gray-400' : ''}`}>Aucun technicentre</p>
              ) : (
                technicentres.map((technicentre) => (
                  <div key={technicentre.id} className={`gof-list-item ${!isGOF ? 'opacity-60' : ''}`}>
                    <div className="gof-list-item-content">
                      <p className={`font-medium ${!isGOF ? 'text-gray-400' : ''}`}>{technicentre.nom}</p>
                      {technicentre.email && (
                        <div className={`text-xs text-light ${!isGOF ? 'text-gray-400' : ''}`}>
                          {technicentre.email.split(',').map((email, idx) => (
                            <div key={idx} className="truncate">{email.trim()}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    {isGOF && (
                      <div className="gof-list-item-actions">
                        <button
                          onClick={() => {
                            setEditingItem(technicentre);
                            setShowPasswordModal(true);
                          }}
                          className="edit-btn"
                          title="Modifier le mot de passe"
                          style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                        >
                          <i className="fas fa-key"></i>
                        </button>
                        <button
                          onClick={() => {
                            setEditingItem(technicentre);
                            setShowTechnicentreModal(true);
                          }}
                          className="edit-btn"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          onClick={() => handleTechnicentreDelete(technicentre.id)}
                          className="delete-btn"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="gof-card">
            <div className="gof-card-header">
              <h3 className="gof-card-title">
                <i className="fas fa-cogs text-purple-600"></i> Prestations
              </h3>
              <button
                onClick={() => {
                  setEditingItem(null);
                  setShowPrestationModal(true);
                }}
                className="btn btn-primary"
                style={{fontSize: '0.875rem', padding: '0.5rem 1rem'}}
              >
                <i className="fas fa-plus mr-1"></i> Ajouter
              </button>
            </div>
            <div className="gof-list">
              {prestations.length === 0 ? (
                <p className="empty-state">Aucune prestation</p>
              ) : (
                prestations.map((prestation) => (
                  <div key={prestation.id} className="gof-list-item">
                    <div className="gof-list-item-content">
                      <p>{prestation.nom}</p>
                    </div>
                    <div className="gof-list-item-actions">
                      <button
                        onClick={() => {
                          setEditingItem(prestation);
                          setShowPrestationModal(true);
                        }}
                        className="edit-btn"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        onClick={() => handlePrestationDelete(prestation.id)}
                        className="delete-btn"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          </div>
        </div>

        {showCampaignModal && (
          <CampaignModal
            item={editingItem}
            onSave={handleCampaignSave}
            onClose={() => {
              setShowCampaignModal(false);
              setEditingItem(null);
            }}
          />
        )}

        {showSiteModal && (
          <SiteModal
            item={editingItem}
            onSave={handleSiteSave}
            onClose={() => {
              setShowSiteModal(false);
              setEditingItem(null);
            }}
          />
        )}

        {showTechnicentreModal && (
          <TechnicentreModal
            item={editingItem}
            onSave={handleTechnicentreSave}
            onClose={() => {
              setShowTechnicentreModal(false);
              setEditingItem(null);
            }}
          />
        )}

        {showPrestationModal && (
          <PrestationModal
            item={editingItem}
            onSave={handlePrestationSave}
            onClose={() => {
              setShowPrestationModal(false);
              setEditingItem(null);
            }}
          />
        )}

        {showPasswordModal && editingItem && (
          <PasswordModal
            technicentre={editingItem}
            onSave={handlePasswordUpdate}
            onClose={() => {
              setShowPasswordModal(false);
              setEditingItem(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

interface CampaignModalProps {
  item: Campaign | null;
  onSave: (data: { nom: string }) => void;
  onClose: () => void;
}

// ===== Composant CampaignModal =====
const CampaignModal: React.FC<CampaignModalProps> = ({ item, onSave, onClose }) => {
  const [name, setName] = useState('');

  useEffect(() => {
    setName(item?.nom || '');
  }, [item]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({ nom: name.trim() });
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="relative mb-6">
          <button onClick={onClose} className="modal-close">
            <i className="fas fa-times"></i>
          </button>
          <h3 className="text-xl font-bold text-dark flex items-center">
            <i className="fas fa-flag mr-3 text-info"></i>
            <span>{item ? 'Modifier la campagne' : 'Nouvelle campagne'}</span>
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="form-label">
              Nom de la campagne <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="form-input"
            />
          </div>
          <button type="submit" className="w-full btn btn-primary">
            <i className="fas fa-save mr-2"></i> Enregistrer
          </button>
        </form>
      </div>
    </div>
  );
};

interface SiteModalProps {
  item: Site | null;
  onSave: (data: { nom: string }) => void;
  onClose: () => void;
}

// ===== Composant SiteModal =====
const SiteModal: React.FC<SiteModalProps> = ({ item, onSave, onClose }) => {
  const [name, setName] = useState('');

  useEffect(() => {
    setName(item?.nom || '');
  }, [item]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({ nom: name.trim() });
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="relative mb-6">
          <button onClick={onClose} className="modal-close">
            <i className="fas fa-times"></i>
          </button>
          <h3 className="text-xl font-bold text-dark flex items-center">
            <i className="fas fa-map-marker-alt mr-3 text-secondary"></i>
            <span>{item ? 'Modifier le lieu' : 'Nouveau lieu d\'intervention'}</span>
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="form-label">
              Nom du lieu <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="form-input"
            />
          </div>
          <button type="submit" className="w-full btn btn-primary">
            <i className="fas fa-save mr-2"></i> Enregistrer
          </button>
        </form>
      </div>
    </div>
  );
};

interface TechnicentreModalProps {
  item: Technicentre | null;
  onSave: (data: { nom: string; email?: string }) => void;
  onClose: () => void;
}

// ===== Composant TechnicentreModal =====
const TechnicentreModal: React.FC<TechnicentreModalProps> = ({ item, onSave, onClose }) => {
  const [name, setName] = useState('');
  const [emails, setEmails] = useState<string[]>(['']);

  useEffect(() => {
    setName(item?.nom || '');
    if (item?.email) {
      const emailList = item.email.split(/[,;\s]+/).filter(e => e.trim());
      setEmails(emailList.length > 0 ? emailList : ['']);
    } else {
      setEmails(['']);
    }
  }, [item]);

  const addEmail = () => {
    setEmails(prev => [...prev, '']);
  };

  const removeEmail = (index: number) => {
    if (emails.length > 1) {
      setEmails(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateEmail = (index: number, value: string) => {
    setEmails(prev => prev.map((email, i) => i === index ? value : email));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    
    const validEmails = emails.filter(email => email.trim()).map(email => email.trim());
    const emailString = validEmails.length > 0 ? validEmails.join(', ') : undefined;
    
    onSave({ 
      nom: name.trim(),
      email: emailString
    });
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="relative mb-6">
          <button onClick={onClose} className="modal-close">
            <i className="fas fa-times"></i>
          </button>
          <h3 className="text-xl font-bold text-dark flex items-center">
            <i className="fas fa-building mr-3 text-orange"></i>
            <span>{item ? 'Modifier le technicentre' : 'Nouveau technicentre'}</span>
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="form-label">
              Nom du technicentre <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">
              <i className="fas fa-envelope mr-1 text-info"></i> Emails de contact
            </label>
            {emails.map((email, index) => (
              <div key={index} className="flex items-center gap-2 mb-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => updateEmail(index, e.target.value)}
                  className="form-input flex-1"
                  placeholder="exemple@technicentre.fr"
                />
                {emails.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeEmail(index)}
                    className="text-danger p-1 transition-normal"
                    title="Supprimer cet email"
                  >
                    <i className="fas fa-trash text-sm"></i>
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addEmail}
              className="text-info text-sm font-weight-600 flex items-center gap-1 mt-2 transition-normal btn-outline"
            >
              <i className="fas fa-plus"></i> Ajouter un email
            </button>
            <p className="text-xs text-light mt-2">
              Les emails seront pré-remplis automatiquement lors de l'envoi d'emails pour ce technicentre
            </p>
          </div>
          <button type="submit" className="w-full btn btn-primary">
            <i className="fas fa-save mr-2"></i> Enregistrer
          </button>
        </form>
      </div>
    </div>
  );
};

interface PrestationModalProps {
  item: Prestation | null;
  onSave: (data: { nom: string }) => void;
  onClose: () => void;
}

// ===== Composant PrestationModal =====
const PrestationModal: React.FC<PrestationModalProps> = ({ item, onSave, onClose }) => {
  const [name, setName] = useState('');

  useEffect(() => {
    setName(item?.nom || '');
  }, [item]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({ nom: name.trim() });
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="relative mb-6">
          <button onClick={onClose} className="modal-close">
            <i className="fas fa-times"></i>
          </button>
          <h3 className="text-xl font-bold text-dark flex items-center">
            <i className="fas fa-cogs mr-3 text-purple"></i>
            <span>{item ? 'Modifier la prestation' : 'Nouvelle prestation'}</span>
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="form-label">
              Nom de la prestation <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="form-input"
            />
          </div>
          <button type="submit" className="w-full btn btn-primary">
            <i className="fas fa-save mr-2"></i> Enregistrer
          </button>
        </form>
      </div>
    </div>
  );
};

interface PasswordModalProps {
  technicentre: Technicentre;
  onSave: (technicentreId: number, newPassword: string) => void;
  onClose: () => void;
}

// ===== Composant PasswordModal =====
const PasswordModal: React.FC<PasswordModalProps> = ({ technicentre, onSave, onClose }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const currentYear = new Date().getFullYear();
  const suggestedPassword = `${technicentre.nom.toLowerCase()}${currentYear}`;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!newPassword.trim()) {
      setError('Le mot de passe est requis');
      return;
    }

    if (newPassword.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    onSave(technicentre.id, newPassword);
  };

  const useSuggestedPassword = () => {
    setNewPassword(suggestedPassword);
    setConfirmPassword(suggestedPassword);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="relative mb-6">
          <button onClick={onClose} className="modal-close">
            <i className="fas fa-times"></i>
          </button>
          <h3 className="text-xl font-bold text-dark flex items-center">
            <i className="fas fa-key mr-3 text-purple"></i>
            <span>Modifier le mot de passe - {technicentre.nom}</span>
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <i className="fas fa-info-circle text-blue-600 mt-1"></i>
              <div className="flex-1">
                <p className="text-sm text-blue-900 font-medium mb-2">
                  Mot de passe suggéré pour {currentYear}:
                </p>
                <div className="flex items-center gap-2">
                  <code className="bg-white px-3 py-2 rounded border border-blue-300 text-blue-900 font-mono text-sm flex-1">
                    {suggestedPassword}
                  </code>
                  <button
                    type="button"
                    onClick={useSuggestedPassword}
                    className="btn btn-primary text-sm"
                    style={{ padding: '0.5rem 1rem' }}
                  >
                    <i className="fas fa-magic mr-1"></i> Utiliser
                  </button>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-800 flex items-center gap-2">
                <i className="fas fa-exclamation-circle"></i>
                {error}
              </p>
            </div>
          )}

          <div>
            <label className="form-label">
              Nouveau mot de passe <span className="text-danger">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className="form-input pr-10"
                placeholder="Minimum 6 caractères"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </button>
            </div>
          </div>

          <div>
            <label className="form-label">
              Confirmer le mot de passe <span className="text-danger">*</span>
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="form-input"
              placeholder="Retapez le mot de passe"
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn btn-secondary"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 btn btn-primary"
            >
              <i className="fas fa-save mr-2"></i> Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GOFPanel;
